import { RefObject } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { openSupportChat } from "@/core/pylon";
import { EnrichedGroupedPeriodSpiffs } from "@/spiffs/spiffs.d";
import { PayrollPeriodCard } from "./payroll-period-card";
import { PayrollPeriodSkeleton } from "./payroll-period-skeleton";
import { PayrollPeriodTimeline } from "./payroll-period-timeline";
import { PayrollPeriodTimelineLine } from "./payroll-period-timeline-line";

interface PayrollPeriodsContentProps {
  periodsWithSpiffs: EnrichedGroupedPeriodSpiffs[];
  isLoading: boolean;
  containerRef: RefObject<HTMLDivElement>;
  firstTimelineRef: RefObject<HTMLDivElement>;
  lastTimelineRef: RefObject<HTMLDivElement>;
  onLockPeriod: (periodId: string) => void;
  flatViewByPeriod: Record<string, boolean>;
  onPeriodToggle: (periodId: string, isFlatView: boolean) => void;
  expandedPeriods: Record<string, boolean>;
  onToggleExpanded: (periodId: string) => void;
}

export const PayrollPeriodsContent = ({
  periodsWithSpiffs,
  isLoading,
  containerRef,
  firstTimelineRef,
  lastTimelineRef,
  onLockPeriod,
  flatViewByPeriod,
  onPeriodToggle,
  expandedPeriods,
  onToggleExpanded,
}: PayrollPeriodsContentProps) => {
  const hasAnySpiffs = periodsWithSpiffs.some(
    (grouped) => grouped.spiffs.length > 0,
  );
  const hasAnyApprovals = periodsWithSpiffs.some((grouped) => grouped.approval);
  const isTimelineEmpty = !isLoading && !hasAnySpiffs && !hasAnyApprovals;

  if (isLoading) {
    return (
      <div ref={containerRef} className="relative w-full pb-32 pt-4">
        <PayrollPeriodTimelineLine
          periodCount={3}
          firstTimelineRef={firstTimelineRef}
          lastTimelineRef={lastTimelineRef}
          containerRef={containerRef}
          className="mt-8"
        />

        <Stack gap="md" className="w-full">
          {[1, 2, 3].map((i, index) => (
            <div
              key={i}
              ref={
                index === 0
                  ? firstTimelineRef
                  : index === 2
                    ? lastTimelineRef
                    : null
              }
            >
              <PayrollPeriodSkeleton />
            </div>
          ))}
        </Stack>
      </div>
    );
  }

  if (isTimelineEmpty) {
    return (
      <Stack align="center" gap="md" className="min-h-[60vh] justify-center">
        <Typography variant="subtitleW1" textColor="secondary">
          No spiffs or approvals to show yet. To get started, set up your spiff
          rules by clicking the button in the top right.
        </Typography>
        <Button variant="secondary" size="sm" onClick={openSupportChat}>
          Chat with our team
        </Button>
      </Stack>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full pb-32">
      <PayrollPeriodTimelineLine
        periodCount={periodsWithSpiffs.length}
        firstTimelineRef={firstTimelineRef}
        lastTimelineRef={lastTimelineRef}
        containerRef={containerRef}
      />

      <Stack gap="md" className="w-full">
        {periodsWithSpiffs.map((groupedPeriod, index) => {
          const isEmpty = groupedPeriod.spiffs.length === 0;
          const isFirst = index === 0;
          const isLast = index === periodsWithSpiffs.length - 1;
          const isExpanded = expandedPeriods[groupedPeriod.period.id] ?? false;

          return (
            <div key={groupedPeriod.period.id} className="relative w-full">
              <div
                ref={
                  isFirst ? firstTimelineRef : isLast ? lastTimelineRef : null
                }
              >
                <PayrollPeriodTimeline
                  period={groupedPeriod.period}
                  isLocked={groupedPeriod.isLocked}
                  isEmpty={isEmpty}
                />
              </div>

              {!isEmpty && (
                <div className="ml-8 mr-2 min-w-0">
                  <PayrollPeriodCard
                    groupedPeriod={groupedPeriod}
                    onLockPeriod={onLockPeriod}
                    canLock={groupedPeriod.canLock}
                    isFlatView={
                      flatViewByPeriod[groupedPeriod.period.id] ?? true
                    }
                    onToggleView={(isFlatView) =>
                      onPeriodToggle(groupedPeriod.period.id, isFlatView)
                    }
                    isExpanded={isExpanded}
                    onToggleExpanded={() =>
                      onToggleExpanded(groupedPeriod.period.id)
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
      </Stack>
    </div>
  );
};
